import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const COLS = ["Project", "Active heads", "Heads by status", "Code format", "Sub-types (active/total)", "Sanctioned", "Working", "Working lines",
  "Vendor Bill heads (before)", "Reimbursement heads (before)", "Heads / sub-types shown now", "Legacy sub-types", "Heads not in budget"];

const wsColor = { approved: "bg-emerald-600", uploaded: "bg-amber-500", none: "bg-slate-400" };

const Row = ({ r }) => (
  <tr className="border-t align-top" data-testid={`elc-row-${r.project_code}`}>
    <td className="p-2 font-semibold">{r.project_code || "—"}<div className="text-[10px] text-slate-500 font-normal">{r.project_name}</div></td>
    <td className="p-2">{r.active_heads}</td>
    <td className="p-2 text-[11px]">{Object.entries(r.heads_by_status).map(([k, v]) => `${k}: ${v}`).join(", ") || "none"}</td>
    <td className="p-2 text-[11px]">{r.head_code_format}</td>
    <td className="p-2">{r.subtypes} / {r.subtypes_total}</td>
    <td className="p-2">{r.sanctioned_status}</td>
    <td className="p-2"><Badge className={wsColor[r.working_status]}>{r.working_status}</Badge></td>
    <td className="p-2">{r.working_lines}</td>
    <td className="p-2">{r.vendor_bill_heads_before}</td>
    <td className="p-2 text-[11px]">{r.reimbursement_heads_before}</td>
    <td className="p-2">{r.heads_shown_now} / {r.subtypes_shown_now}</td>
    <td className="p-2">{r.legacy_subtypes}</td>
    <td className="p-2 text-[11px]">{r.heads_not_in_budget.length ? r.heads_not_in_budget.map(h => `${h.code} (${h.status || "no status"})`).join(", ") : "—"}</td>
  </tr>
);

export const ExpenseLinesCheck = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/v2/expense-lines-check").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const heads = data.rows.filter(r => r.project_code).flatMap(r => r.heads.map(h => ({ ...h, project_code: r.project_code })));
  return (
    <Card data-testid="expense-lines-check">
      <CardContent className="p-3 space-y-4">
        <p className="text-xs text-slate-500 mb-2">Read-only. "Before" columns show what the old dropdowns loaded; "shown now" is what the shared expense-lines list returns.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500"><tr>{COLS.map(c => <th key={c} className="p-2 font-medium">{c}</th>)}</tr></thead>
            <tbody>{data.rows.map(r => <Row key={r.project_id} r={r} />)}</tbody>
          </table>
        </div>
        <DataFixes fixes={data.data_fixes} />
        <div className="overflow-x-auto" data-testid="elc-heads-table">
          <div className="text-sm font-semibold mb-1">All project heads</div>
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500"><tr>{["Project", "Code", "Name", "Category", "Status", "is_active", "Treated active", "project_id", "Created via", "Sub-types"].map(c => <th key={c} className="p-2 font-medium">{c}</th>)}</tr></thead>
            <tbody>{heads.map(h => (
              <tr key={`${h.project_id}-${h.code}`} className="border-t" data-testid={`elc-head-${h.project_code}-${h.code}`}>
                <td className="p-2">{h.project_code}</td><td className="p-2 font-mono">{h.code}</td><td className="p-2">{h.name}</td>
                <td className="p-2">{h.category_code || "—"}</td><td className="p-2">{h.status}</td><td className="p-2">{String(h.is_active ?? "—")}</td>
                <td className="p-2">{h.treated_active ? "yes" : <span className="text-rose-600">no</span>}</td>
                <td className="p-2 font-mono text-[10px]">{h.project_id}</td><td className="p-2">{h.created_via}</td><td className="p-2">{h.subtypes}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const DataFixes = ({ fixes }) => {
  if (!fixes?.length) return <div className="text-xs text-slate-500" data-testid="elc-data-fixes">No automatic head fixes have run.</div>;
  return (
    <div className="text-xs" data-testid="elc-data-fixes">
      <div className="text-sm font-semibold mb-1">Automatic head fixes (deploy-time)</div>
      {fixes.map(f => (
        <div key={f.ran_at} className="mb-1">{f.ran_at?.slice(0, 16).replace("T", " ")}: {f.changed} head(s) — {f.changes.map(c => `${c.code} (${Object.keys(c.after).join("/")})`).join(", ")}</div>
      ))}
    </div>
  );
};
